import { compareHlc } from './hlc.js';
/**
 * Слияние: LWW по каждому полю отдельно.
 *
 * Разрешение по полям, а не по записи, снимает большую часть конфликтов вовсе:
 * поставить дедлайн с ноутбука и закрыть задачу с телефона — правки разных
 * полей, они обе применяются (раздел 3.2 архитектуры). Требование 3.14.6
 * выполняется тем, что результат детерминирован и одинаков на сервере и на всех
 * клиентах — отсюда же требование к функции быть чистой.
 */
/** Поля, которые не участвуют в LWW: их ведёт сервер, а не правки пользователя. */
const SERVICE_FIELDS = new Set(['id', 'user_id', 'field_clocks', 'last_seq']);
export function mergeRecord(local, incoming) {
    if (local === undefined) {
        // last_seq выставляется и здесь: иначе запись, собранная из одной мутации,
        // отличалась бы набором ключей от такой же, но прошедшей через слияние, —
        // и конвергенция ломалась бы на пустом месте.
        return {
            ...incoming,
            field_clocks: { ...incoming.field_clocks },
            last_seq: incoming.last_seq ?? 0,
        };
    }
    const merged = { ...local };
    const clocks = { ...local.field_clocks };
    for (const [field, clock] of Object.entries(incoming.field_clocks)) {
        if (SERVICE_FIELDS.has(field))
            continue;
        const known = clocks[field];
        // Строго больше, а не «не меньше»: одинаковые часы означают одну и ту же
        // мутацию — часы устройства не выдают одно значение дважды. Ослабить
        // сравнение значило бы сделать результат зависимым от порядка доставки.
        if (known === undefined || compareHlc(clock, known) > 0) {
            merged[field] = incoming[field];
            clocks[field] = clock;
        }
    }
    merged.field_clocks = clocks;
    merged.last_seq = Math.max(local.last_seq ?? 0, incoming.last_seq ?? 0);
    return merged;
}
/**
 * Разворачивает мутацию в запись: тем же кодом это делает сервер в `sync/push` и
 * клиент, применяющий свою правку до ответа сервера. Второго пути записи нет —
 * иначе они разъедутся (раздел 2 архитектуры).
 *
 * Удаление — надгробие: `deleted_at` со своими часами. Более поздний upsert
 * воскрешает запись, поэтому отмена удаления не требует ничего особенного.
 * Чем именно отличаются три вида мутаций — в `mutationFields` ниже.
 */
export function applyMutation(local, mutation, seed) {
    const fields = mutationFields(mutation);
    const clocks = {};
    for (const field of Object.keys(fields)) {
        if (SERVICE_FIELDS.has(field))
            continue;
        clocks[field] = mutation.hlc;
    }
    const incoming = {
        ...(local ?? seed ?? {}),
        ...fields,
        id: mutation.entity_id,
        field_clocks: clocks,
    };
    return mergeRecord(local, incoming);
}
/**
 * Что мутация меняет в записи — и, стало быть, каким полям она проставит часы.
 *
 * Наружу вынесено ради отката: клиент, получив отказ сервера, снимает часы
 * ровно с этих полей. Спрашивать их здесь, а не перечислять на месте, —
 * единственный способ не разъехаться с тем, что мутация делает на самом деле:
 * `deleted_at` трогают все три вида, и о нём забыли бы первым.
 *
 * Все три вида трогают `deleted_at`, и разница между ними только во времени,
 * которое в него попадает:
 *
 * - `upsert` — `null`: любая правка снимает надгробие, поэтому возврат из
 *   корзины не требует ничего особенного;
 * - `delete` — время самой мутации: с него пойдёт отсчёт срока хранения;
 * - `purge` — время, которое срок уже пережило. Стирание насовсем выражено
 *   не отдельным полем, а сдвигом даты за границу срока: тогда запись
 *   пропадает из корзины на всех устройствах обычным слиянием, а физически её
 *   снимет та же уборка, что снимает надгробия по истечении срока. Второго
 *   механизма забвения в системе не появляется.
 */
export function mutationFields(mutation) {
    const ms = decodeMs(mutation.hlc);
    switch (mutation.kind) {
        case 'delete':
            return { deleted_at: new Date(ms).toISOString() };
        case 'purge':
            // Сутки сверх срока, а не ровно срок: граница «уже пора» проверяется
            // строгим сравнением и у корзины, и у уборки.
            return { deleted_at: new Date(ms - (TOMBSTONE_TTL_DAYS + 1) * DAY_MS).toISOString() };
        default:
            return { ...mutation.fields, deleted_at: null };
    }
}
/**
 * Время удаления берётся из часов мутации, а не из `Date.now()`: иначе один и тот
 * же журнал, проигранный на двух устройствах, давал бы разные `deleted_at`, и
 * слияние перестало бы сходиться.
 */
function decodeMs(hlc) {
    return Number.parseInt(hlc.slice(0, hlc.indexOf('-')), 36);
}
/** Жива ли запись: надгробие не удаляет строку, а помечает её. */
export function isAlive(record) {
    return record.deleted_at == null;
}
/**
 * Сколько живёт надгробие, прежде чем сервер уберёт запись физически.
 *
 * Срок стоит здесь, а не в уборщике на сервере, потому что у него два
 * потребителя: уборка считает по нему момент удаления, а корзина в клиенте —
 * что ещё можно вернуть (3.18.6). Разъехавшись, они начнут врать в разные
 * стороны: корзина предложит вернуть то, чего на сервере уже нет, — и правка
 * уедет в карантин с `missing_field`, потому что воскрешать будет нечего.
 */
export const TOMBSTONE_TTL_DAYS = 30;
const DAY_MS = 86_400_000;
/**
 * Сколько дней надгробию осталось до уборки. Ноль — срок вышел: запись либо
 * уже убрана, либо будет убрана ближайшим проходом.
 *
 * Испорченная дата тоже даёт ноль. Это осознанно: непонятно когда удалённая
 * задача — не та, про которую можно обещать «вернём».
 */
export function daysLeftInTrash(deletedAt, now) {
    const left = Date.parse(deletedAt) + TOMBSTONE_TTL_DAYS * DAY_MS - now.getTime();
    if (!Number.isFinite(left) || left <= 0)
        return 0;
    return Math.ceil(left / DAY_MS);
}
/**
 * Лежит ли запись в корзине: надгробие есть, и срок ещё не вышел.
 *
 * Надгробия старше срока с локальной копии сами не исчезают — снимок сливается
 * с ней, а не подменяет её, — поэтому фильтровать по сроку обязано место
 * показа, а не приход данных.
 */
export function isInTrash(record, now) {
    return record.deleted_at != null && daysLeftInTrash(record.deleted_at, now) > 0;
}
//# sourceMappingURL=merge.js.map